import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, TrendingUp, Clock, User, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { getAllArticles, searchArticles } from '../services/news';
import { Layout, Button, OptimizedImage } from '../components/common';

const ARTICLES_PER_PAGE = 6;

const News: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  
  const categories = ['All', 'Announcements', 'Products', 'Thought Leadership', 'Research', 'Company'];
  
  const allArticles = useMemo(() => getAllArticles(), []);
  
  const filteredArticles = useMemo(() => {
    let articles = searchTerm ? searchArticles(searchTerm, allArticles) : allArticles;
    
    if (selectedCategory !== 'All') {
      articles = articles.filter(a => a.category === selectedCategory);
    }
    
    return articles;
  }, [searchTerm, selectedCategory, allArticles]);
  
  const totalPages = Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE);
  
  const paginatedArticles = useMemo(() => {
    const start = (currentPage - 1) * ARTICLES_PER_PAGE;
    return filteredArticles.slice(start, start + ARTICLES_PER_PAGE);
  }, [filteredArticles, currentPage]);
  
  const featuredArticles = paginatedArticles.filter(article => article.featured);
  const regularArticles = paginatedArticles.filter(article => !article.featured);
  
  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const handleArticleClick = (slug: string) => {
    navigate(`/news/${slug}`);
  };

  return (
    <Layout>
      {/* Hero Section with background image */}
      <section className="relative px-4 md:px-16 py-16 md:py-24 lg:py-28 bg-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="/images/015.news.main.jpeg"
            alt="News and updates"
            className="absolute inset-0 w-full h-full"
            priority={true}
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="container mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold font-gabarito text-white mb-8 tracking-tight">
            <span className="text-[#C97B5A]">News</span> & Updates
          </h1>
          <p className="text-xl md:text-2xl text-white mb-12 max-w-4xl mx-auto leading-relaxed font-cairo">
            Stay updated with the latest developments in AI, product announcements, and insights from the MindClave team.
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-Color-Neutral-Dark w-5 h-5" />
            <input
              type="text"
              placeholder="Search articles, topics, or authors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-Color-Neutral-Medium rounded-lg focus:ring-2 focus:ring-[#C97B5A] focus:border-transparent text-lg font-cairo bg-white"
            />
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-10 bg-white border-b">
        <div className="container mx-auto px-4 md:px-16">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-lg text-sm font-semibold transition-colors font-cairo ${
                  selectedCategory === category
                    ? 'bg-[#C97B5A] text-white'
                    : 'bg-Color-Neutral-Light text-Color-Neutral-Dark hover:bg-Color-Neutral-Medium'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Content */}
      <section className="py-16 md:py-24 bg-Color-Scheme-1-Background">
        <div className="container mx-auto px-4 md:px-16">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-24">
              <h3 className="text-3xl font-bold font-gabarito text-Color-Scheme-1-Text mb-6 tracking-tight">
                {searchTerm ? 'No search results' : 'No articles found'}
              </h3>
              <p className="text-Color-Neutral-Dark text-lg font-cairo">
                {searchTerm 
                  ? `No articles found matching "${searchTerm}". Try different keywords.`
                  : `No articles in the ${selectedCategory} category yet.`
                }
              </p>
            </div>
          ) : (
            <>
              {/* Featured Stories */}
              {featuredArticles.length > 0 && (
                <div className="mb-20">
                  <div className="flex items-center mb-12">
                    <TrendingUp className="w-6 h-6 text-[#C97B5A] mr-2" />
                    <h2 className="text-3xl font-bold font-gabarito text-Color-Scheme-1-Text tracking-tight">Featured Stories</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {featuredArticles.map((article) => (
                      <article 
                        key={article.id} 
                        className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                        onClick={() => handleArticleClick(article.slug)}
                      >
                        <div className="p-8">
                          <div className="flex items-center justify-between mb-6">
                            <span className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold bg-[#F5E6D3] text-[#C97B5A] font-cairo">
                              {article.category}
                            </span>
                            <div className="flex items-center text-sm text-Color-Neutral-Dark font-cairo">
                              <Calendar className="w-4 h-4 mr-1" />
                              {formatDate(article.date)}
                            </div>
                          </div>
                          <h3 className="text-2xl md:text-3xl font-bold font-gabarito text-Color-Scheme-1-Text mb-4 group-hover:text-[#C97B5A] transition-colors tracking-tight">
                            {article.title}
                          </h3>
                          <p className="text-Color-Neutral-Dark mb-6 line-clamp-3 font-cairo">{article.excerpt}</p>
                          
                          <div className="flex items-center justify-between pt-6 border-t border-Color-Neutral-Light">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center text-sm text-Color-Neutral-Dark font-cairo">
                                <User className="w-4 h-4 mr-2" />
                                <span>{article.author}</span>
                              </div>
                              <div className="flex items-center text-sm text-Color-Neutral-Dark font-cairo">
                                <Clock className="w-4 h-4 mr-2" />
                                <span>{article.readTime}</span>
                              </div>
                            </div>
                            <Button variant="secondary" size="sm">
                              Read Article
                            </Button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {/* Latest Articles */}
              {regularArticles.length > 0 && (
                <div>
                  <h2 className="text-3xl font-bold font-gabarito text-Color-Scheme-1-Text mb-12 tracking-tight">Latest Articles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {regularArticles.map((article) => (
                      <article 
                        key={article.id} 
                        className="group bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer"
                        onClick={() => handleArticleClick(article.slug)}
                      >
                        <div className="p-6">
                          <div className="flex items-center justify-between mb-4">
                            <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold bg-Color-Neutral-Light text-Color-Neutral-Dark font-cairo">
                              {article.category}
                            </span>
                            <div className="flex items-center text-xs text-Color-Neutral-Dark font-cairo">
                              <Calendar className="w-3 h-3 mr-1" />
                              {formatDate(article.date)}
                            </div>
                          </div>
                          <h3 className="text-xl font-bold font-gabarito text-Color-Scheme-1-Text mb-3 group-hover:text-[#C97B5A] transition-colors tracking-tight">
                            {article.title}
                          </h3>
                          <p className="text-Color-Neutral-Dark mb-4 line-clamp-2 font-cairo text-sm">{article.excerpt}</p>
                          
                          <div className="flex items-center justify-between pt-4 border-t border-Color-Neutral-Light">
                            <div className="flex items-center space-x-3">
                              <div className="flex items-center text-xs text-Color-Neutral-Dark font-cairo">
                                <User className="w-3 h-3 mr-1" />
                                <span>{article.author}</span>
                              </div>
                              <div className="flex items-center text-xs text-Color-Neutral-Dark font-cairo">
                                <Clock className="w-3 h-3 mr-1" />
                                <span>{article.readTime}</span>
                              </div>
                            </div>
                            <Button variant="secondary" size="sm" className="text-xs">
                              Read
                            </Button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center mt-16 space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-lg transition-colors ${
                      currentPage === 1
                        ? 'bg-Color-Neutral-Light text-Color-Neutral-Medium cursor-not-allowed'
                        : 'bg-white text-Color-Neutral-Dark hover:bg-Color-Neutral-Light border border-Color-Neutral-Medium'
                    }`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  
                  <div className="flex items-center space-x-2">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 rounded-lg font-medium transition-colors font-cairo ${
                          page === currentPage
                            ? 'bg-[#C97B5A] text-white'
                            : 'bg-white text-Color-Neutral-Dark hover:bg-Color-Neutral-Light border border-Color-Neutral-Medium'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-lg transition-colors ${
                      currentPage === totalPages
                        ? 'bg-Color-Neutral-Light text-Color-Neutral-Medium cursor-not-allowed'
                        : 'bg-white text-Color-Neutral-Dark hover:bg-Color-Neutral-Light border border-Color-Neutral-Medium'
                    }`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default News;